import { z } from 'zod'

const name = (label: string) => z.string().trim().min(1, `${label} is required`).max(60, `${label} is too long`)

export const addressSchema = z
  .object({
    line1: z.string().trim().max(120, 'Address is too long'),
    line2: z.string().trim().max(120, 'Address is too long'),
    city: z.string().trim().max(80, 'City is too long'),
    region: z.string().trim().max(80, 'State or region is too long'),
    postalCode: z.string().trim().max(20, 'Postal code is too long'),
    country: z.string().trim().max(80, 'Country is too long'),
  })
  .superRefine((address, context) => {
    // Optional as a whole: an untouched address is fine, a started one must be complete enough to post to.
    if (!Object.values(address).some(Boolean)) return
    const required = { line1: 'Street address', city: 'City', postalCode: 'Postal code', country: 'Country' } as const
    for (const [field, label] of Object.entries(required)) {
      if (!address[field as keyof typeof required]) context.addIssue({ code: 'custom', path: [field], message: `${label} is required` })
    }
  })

/** The backend's own rule: an optional leading +, then a digit, then digits and the usual separators, up to 25 characters. */
export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'Phone number is required')
  .max(25, 'Phone number is too long')
  .regex(/^\+?[0-9][0-9 ()\-.]*$/, 'Enter a valid phone number')

export const personSchema = z.object({
  firstName: name('First name'),
  lastName: name('Last name'),
  email: z.string().trim().min(1, 'Email is required').max(255, 'Email is too long').pipe(z.email('Enter a valid email address')),
  phoneNumber: phoneSchema,
})

export const accountNameSchema = z.string().trim().min(2, 'Enter at least 2 characters').max(120, 'Name is too long')

export type PersonValues = z.input<typeof personSchema>

/** First problem in a zod error as a sentence, or null when valid. */
export function firstIssue<T>(result: { success: true; data: T } | { success: false; error: z.ZodError }): string | null {
  return result.success ? null : (result.error.issues[0]?.message ?? 'Check the form.')
}
